import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { 
  Eye, 
  Search, 
  Radio, 
  Shield,
  CheckCircle,
  AlertTriangle,
  Lock,
  Headphones,
  Camera,
  Zap
} from 'lucide-react';

const CounterIntelligencePage = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const services = [
    {
      title: "Advanced Spy Device Detection",
      description: "State-of-the-art equipment and techniques to detect and neutralize sophisticated surveillance devices and bugs.",
      icon: Search,
      image: "https://images.pexels.com/photos/8728559/pexels-photo-8728559.jpeg",
      features: [
        "RF spectrum analysis and detection",
        "Non-linear junction detection (NLJD)",
        "Thermal imaging for concealed devices",
        "Audio and video bug detection",
        "GPS tracking device identification"
      ]
    },
    {
      title: "Technical Surveillance Countermeasures (TSCM)",
      description: "Comprehensive TSCM sweeps and electronic surveillance detection for secure facilities and sensitive areas.",
      icon: Radio,
      image: "https://images.pexels.com/photos/5475750/pexels-photo-5475750.jpeg",
      features: [
        "Complete facility security sweeps",
        "Communication line analysis",
        "Electrical system inspection",
        "HVAC and structural analysis",
        "Periodic security maintenance"
      ]
    },
    {
      title: "Room Debugging & Sweep Services",
      description: "Professional debugging services for offices, conference rooms, and sensitive meeting spaces.",
      icon: Shield,
      image: "https://images.pexels.com/photos/16053029/pexels-photo-16053029.jpeg",
      features: [
        "Physical inspection and search",
        "Electronic surveillance detection",
        "Communication security assessment",
        "Environmental monitoring",
        "Security protocol recommendations"
      ]
    },
    {
      title: "Anti-Spy Device Installation",
      description: "Installation and deployment of counter-surveillance equipment to protect against espionage activities.",
      icon: Eye,
      image: "https://images.unsplash.com/photo-1589935447067-5531094415d1",
      features: [
        "White noise generators",
        "RF jammers and blockers",
        "Secure communication systems",
        "Counter-surveillance cameras",
        "Physical security barriers"
      ]
    }
  ];

  const specialEquipment = [
    {
      name: "Professional RF Detectors",
      description: "Advanced radio frequency detection equipment"
    },
    {
      name: "Non-Linear Junction Detectors",
      description: "Detect electronic devices regardless of power state"
    },
    {
      name: "Spectrum Analyzers",
      description: "Professional-grade spectrum analysis tools"
    },
    {
      name: "Thermal Imaging Cameras",
      description: "Detect heat signatures from concealed devices"
    },
    {
      name: "Audio Analysis Equipment",
      description: "Identify and locate audio surveillance devices"
    },
    {
      name: "Digital Forensics Tools",
      description: "Analyze and document surveillance evidence"
    }
  ];

  const clientTypes = [
    "Government Agencies",
    "Military Installations", 
    "Intelligence Services",
    "Corporate Executives",
    "Legal Firms",
    "Diplomatic Missions"
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section 
        className="section-padding relative overflow-hidden"
        style={{
          backgroundImage: `url('https://images.pexels.com/photos/8728559/pexels-photo-8728559.jpeg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-purple-900/90 to-near-black/90 z-0" />
        <div className="absolute inset-0 grid-pattern opacity-20 z-1" />
        
        <div className="container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-700 rounded-16 flex items-center justify-center">
                <Eye className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="heading-font text-4xl md:text-6xl font-bold text-off-white">
                  Counter-Intelligence & <span className="text-royal-gold">Surveillance</span>
                </h1>
              </div>
            </div>
            
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed max-w-3xl">
              Specialized equipment and services for counter-intelligence operations, surveillance 
              detection, and comprehensive security assessments. Protect your sensitive information 
              from espionage activities.
            </p>

          </motion.div>
        </div>
      </section>

      {/* Services Detail */}
      <section className="section-padding bg-gray-dark" ref={ref}>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="heading-font text-4xl md:text-5xl font-bold mb-6 text-off-white">
              Counter-Intelligence <span className="text-royal-gold">Services</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Advanced counter-surveillance and technical security countermeasures 
              to protect against espionage and unauthorized surveillance activities.
            </p>
          </motion.div>

          <div className="space-y-20">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                }`}
              >
                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-700 rounded-16 flex items-center justify-center">
                      <service.icon className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="heading-font text-3xl md:text-4xl font-bold text-off-white">
                        {service.title}
                      </h3>
                    </div>
                  </div>
                  
                  <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                    {service.description}
                  </p>
                  
                  {/* Features List */}
                  <div className="space-y-3">
                    {service.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center space-x-3">
                        <CheckCircle className="w-5 h-5 text-royal-gold flex-shrink-0" />
                        <span className="text-gray-200">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Image */}
                <div className={index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}>
                  <div className="relative rounded-24 overflow-hidden group">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-80 object-cover transition-transform duration-300 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-near-black/60 to-transparent" />
                    <div className="absolute bottom-6 left-6">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-700 rounded-12 flex items-center justify-center">
                        <service.icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Special Equipment */}
      <section className="section-padding bg-near-black">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="heading-font text-4xl md:text-5xl font-bold mb-6 text-off-white">
              Specialized <span className="text-royal-gold">Equipment</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              State-of-the-art counter-surveillance equipment and detection systems 
              used by intelligence agencies and security professionals worldwide.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {specialEquipment.map((equipment, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="service-card group"
              >
                <div className="flex items-center space-x-3 mb-4">
                  <Zap className="w-6 h-6 text-royal-gold" />
                  <h3 className="text-lg font-semibold text-off-white group-hover:text-royal-gold transition-colors duration-300">
                    {equipment.name}
                  </h3>
                </div>
                <p className="text-gray-300 text-sm leading-relaxed">
                  {equipment.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Client Types */}
      {/* <section className="section-padding bg-gray-dark">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              <h2 className="heading-font text-4xl md:text-5xl font-bold mb-6 text-off-white">
                Protecting <span className="text-royal-gold">High-Value Targets</span>
              </h2>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Our counter-intelligence services protect the most sensitive operations 
                and facilities from sophisticated surveillance and espionage threats.
              </p>
              
              <div className="space-y-3 mb-8">
                {clientTypes.map((client, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <Shield className="w-5 h-5 text-royal-gold flex-shrink-0" />
                    <span className="text-gray-200">{client}</span>
                  </div>
                ))}
              </div>
              
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="space-y-6"
            >
              <div className="glass-morphism p-8 rounded-24">
                <div className="flex items-center space-x-4 mb-6">
                  <Lock className="w-8 h-8 text-royal-gold" />
                  <h3 className="text-2xl font-semibold text-off-white">Classified Operations</h3>
                </div>
                <p className="text-gray-300">
                  Our team maintains the highest security clearances to support 
                  classified counter-intelligence operations and sensitive facility sweeps.
                </p>
              </div>

              <div className="glass-morphism p-8 rounded-24">
                <div className="flex items-center space-x-4 mb-6">
                  <AlertTriangle className="w-8 h-8 text-royal-gold" />
                  <h3 className="text-2xl font-semibold text-off-white">Emergency Response</h3>
                </div>
                <p className="text-gray-300">
                  24/7 availability for emergency TSCM sweeps and counter-surveillance 
                  operations when immediate security threats are detected.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section> */}

    </div>
  );
};

export default CounterIntelligencePage;